import { Box, Divider, List } from '@mui/material'
import React, { useMemo } from 'react'

import { addRecentSearch } from '#/components/AdvancedSearch/Logic/recentSearches'
import {
  isPageCategory,
  RECENT_GROUP_KEY,
  resolveSearchResultItem,
} from '#/components/AdvancedSearch/Logic/searchResults'
import { ResultGroupsProps } from '#/components/AdvancedSearch/types'
import { ResultIconBadge } from '#/components/AdvancedSearch/View/Icons'
import { EntityLocateButton, SearchResultRow } from '#/components/AdvancedSearch/View/Item'
import { GroupTitle } from '#/components/AdvancedSearch/View/Title'
import { useFarmSlug } from '#/store/ducks/farm'
import { Sentry } from '#/store/services/Sentry'

/**
 * Lista de resultados agrupados por categoria, com entidades e páginas no mesmo formato
 * A resolução de cada item vem de resolveSearchResultItem
 */
export function ResultGroups({
  activeIndex,
  groups,
  handleClose,
  onClearRecent,
  onViewAll,
  scope,
  searchTerm,
  setActiveIndex,
}: ResultGroupsProps) {
  const farmSlug = useFarmSlug()

  // Índice do item na lista achatada, usado para destacar a linha ativa na navegação por teclado
  const flatIndexByKey = useMemo(() => {
    const indexByKey = new Map<string, number>()
    groups.forEach(group => group.items.forEach(item => indexByKey.set(item.key, indexByKey.size)))
    return indexByKey
  }, [groups])

  return (
    <>
      {groups.map(group => {
        const groupKey = group.key
        const isRecentGroup = groupKey === RECENT_GROUP_KEY

        return (
          <Box key={groupKey}>
            <GroupTitle
              color={group.color}
              count={group.totalCount}
              label={group.label}
              isCapped={group.isCapped}
              shownCount={group.items.length}
              onViewAll={
                group.isCapped && isPageCategory(groupKey) ? () => onViewAll(groupKey) : undefined
              }
              onClearFilter={group.isActiveFilter ? () => onViewAll(null) : undefined}
              onClear={isRecentGroup ? onClearRecent : undefined}
            />
            <List>
              {group.items.map((result, position) => {
                const index = flatIndexByKey.get(result.key) ?? position
                const isActive = index === activeIndex
                const last = position === group.items.length - 1
                const { recentSearchItem, resolved } = resolveSearchResultItem(result)

                const resourceType =
                  resolved.icon.source === 'page' ? resolved.icon.category : resolved.icon.entity

                const handleMouseMove = () => {
                  if (index !== activeIndex) setActiveIndex(index)
                }

                const handleClick = () => {
                  if (recentSearchItem) addRecentSearch(recentSearchItem, scope)
                  Sentry.hitSearch({
                    isPage: resolved.icon.source === 'page',
                    isRecent: result.kind === 'recent',
                    link: resolved.link,
                    resourceType,
                  })
                  handleClose()
                }

                return (
                  <React.Fragment key={resolved.key}>
                    <SearchResultRow
                      icon={<ResultIconBadge icon={resolved.icon} />}
                      label={resolved.label}
                      crumbLabel={resolved.crumbLabel}
                      description={resolved.description}
                      link={resolved.link}
                      onClick={handleClick}
                      onMouseMove={handleMouseMove}
                      isActive={isActive}
                      searchTerm={searchTerm}
                      action={
                        resolved.locate && (
                          <EntityLocateButton
                            farmSlug={farmSlug}
                            locate={resolved.locate}
                            onLocate={handleClose}
                          />
                        )
                      }
                    />
                    {!last && <Divider />}
                  </React.Fragment>
                )
              })}
            </List>
          </Box>
        )
      })}
    </>
  )
}
